import { useMemo } from 'react';
import { AssortmentItemMinimal, CentralAssortmentItem } from '@retail/my-assortment/types';
import { useMyAssortmentTranslation } from '@retail/my-assortment/i18n';
import { GridColDef } from '@mui/x-data-grid-premium';
import { ExternalItemSource } from '@retail/products/components';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { AssortmentTableActions } from '../AssortmentTable/AssortmentTableActions';
import { AssortmentItemStatus } from '../AssortmentTable/AssortmentItemStatus';
import { useItemInfoColumns } from '../AssortmentTable';
import { AssortmentTagsCell } from './AssortmentTagsCell';
import { OrgUnitType } from '@retail/mgpris/config-types';

type GridColAssortmentItem = GridColDef<CentralAssortmentItem>;

export interface ShowColumnProps {
  orgUnitType: OrgUnitType;
}
interface Props {
  editItem?: (item: CentralAssortmentItem) => void;
  editTags?: (item: CentralAssortmentItem) => void;
  deleteItem?: (itemId: number) => void;
  addItem?: (items: AssortmentItemMinimal) => void;
  replaceItem?: (item: CentralAssortmentItem) => void;
  deleteAssortmentItemTag?: (tag: SupplierItemAssortmentTag, mgSupplierItemId: number) => void;
  selectSupplier: (supplierId: number) => void;
  selectProductGroup: (groupNumber: string) => void;
  selectRowId?: (rowId: number) => void;
  externalItemSource: ExternalItemSource;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
  sortable: false,
};

export const useCentralAssortmentColumns = ({
  editItem,
  editTags,
  deleteItem,
  addItem,
  replaceItem,
  deleteAssortmentItemTag,
  selectSupplier,
  selectProductGroup,
  selectRowId,
  externalItemSource,
}: Props): GridColAssortmentItem[] => {
  const { t } = useMyAssortmentTranslation();

  const {
    itemIdsColumn,
    itemNameColumn,
    supplierColumn,
    productGroupColumn,
    itemStatusColumn,
    supplierItemPriorityColumn,
    discontinuationColumn,
  } = useItemInfoColumns<CentralAssortmentItem>({
    selectSupplier,
    selectProductGroup,
    externalItemSource,
  });

  return useMemo(
    () => [
      {
        ...itemStatusColumn,
        renderCell: ({ row }) => (
          <AssortmentItemStatus
            inAssortment={row.inAssortment}
            priorityValidFrom={row.priority.validFrom}
            priorityValidTo={row.priority.validTo}
            replacementPriority={row.replacementPriority}
            externalItemSource={externalItemSource}
          />
        ),
      },
      itemIdsColumn,
      itemNameColumn,
      supplierItemPriorityColumn,
      supplierColumn,
      productGroupColumn,
      {
        ...baseColumnProps,
        width: 220,
        field: 'tagName',
        headerName: t('myAssortment.columns.subAssortments'),
        renderCell: ({ row }) => (
          <AssortmentTagsCell
            assortmentTags={row.assortmentTags}
            deleteTag={
              deleteAssortmentItemTag
                ? (tag) => deleteAssortmentItemTag(tag, row.mgSupplierItemId)
                : undefined
            }
          />
        ),
      },
      discontinuationColumn,
      {
        ...baseColumnProps,
        field: 'addedBy',
        headerName: t('myAssortment.columns.addedBy'),
        renderCell: ({ row }) =>
          row.createdBy ? `${row.createdBy.givenName} ${row.createdBy.familyName}` : '-',
      },
      {
        ...baseColumnProps,
        width: 180,
        field: 'mainSupplierName',
        headerName: t('myAssortment.columns.mainSupplierName'),
        renderCell: ({ row }) => row.mainSupplierName ?? '-',
      },
      {
        ...baseColumnProps,
        field: 'actions',
        width: 75,
        sortable: false,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <AssortmentTableActions
            viewItemProductCard={selectRowId ? () => selectRowId(row.mgSupplierItemId) : undefined}
            addItemToAssortment={
              addItem && !row.inAssortment ? () => addItem({ ...row }) : undefined
            }
            editItem={editItem ? () => editItem(row) : undefined}
            editTags={editTags ? () => editTags(row) : undefined}
            deleteItem={deleteItem ? () => deleteItem(row.mgSupplierItemId) : undefined}
            replaceItem={replaceItem ? () => replaceItem(row) : undefined}
            isAlreadyReplaced={!!row.replacementPriority}
          />
        ),
      },
    ],
    [
      itemStatusColumn,
      itemIdsColumn,
      itemNameColumn,
      supplierItemPriorityColumn,
      supplierColumn,
      productGroupColumn,
      t,
      discontinuationColumn,
      deleteAssortmentItemTag,
      selectRowId,
      editItem,
      editTags,
      deleteItem,
      addItem,
      replaceItem,
      externalItemSource,
    ]
  );
};
